// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * Advisory repository utilities for cache diff and transform.
 *
 * Used by advisory API v2 endpoints (positions, historical balances, transactions,
 * accounts debt) to read or write the advisory repository slice. All use per-IBAN
 * diff helpers. All diff functions are pure: they return cached data and the list
 * of IBANs that need to be fetched; they do not accept a `forced` parameter.
 * Refetch behaviour is handled by the API layer (e.g. forceRefetch on the raw
 * request).
 */

import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import type {
  RootAdvisoryRepositoryState,
  AdvisoryPositionsState,
  AdvisoryBalancesHistoricalState,
  AdvisoryTransactionsState,
  AdvisoryAccountsDebtState,
} from '@shared/data-access/interfaces/redux/advisory-repository-state-types';
import type {
  AccountsPositionsData,
  AccountsTransactionsData,
} from '@shared/data-access/interfaces/api/brokerage/brokerage';
import type {
  AccountHistoricalBalances,
  AdvisoryAccountsDebt,
  AdvisoryDebtAccount,
} from '@shared/data-access/types/brokerage-types';
import {
  selectAdvisoryPositionsByIbans,
  selectAdvisoryBalancesHistoricalByIbans,
  selectAdvisoryTransactionsByIbans,
  selectAdvisoryAccountsDebtByIbans,
} from './advisory-repository-selectors';
import {
  generateAdvisoryBalancesHistoricalCacheKey,
  generateAdvisoryPositionsCacheKey,
  generateAdvisoryTransactionsCacheKey,
  generateAdvisoryAccountsDebtCacheKey,
} from './advisory-repository-state';

/** Result of checking cache for positions */
export interface CachedPositionsDiff {
  /** Positions data that are already in cache */
  cached: AccountsPositionsData[];
  /** IBANs that are not in cache and need to be fetched from API */
  missingIbans: string[];
}

/** Result of checking cache for balances historical */
export interface CachedBalancesHistoricalDiff {
  /** Balances historical data that are already in cache */
  cached: AccountHistoricalBalances[];
  /** IBANs that are not in cache and need to be fetched from API */
  missingIbans: string[];
}

/** Result of checking cache for transactions */
export interface CachedTransactionsDiff {
  /** Transactions data that are already in cache */
  cached: AccountsTransactionsData[];
  /** IBANs that are not in cache and need to be fetched from API */
  missingIbans: string[];
}

/** Result of checking cache for accounts debt */
export interface CachedAccountsDebtDiff {
  /** Debt account data that are already in cache */
  cached: AdvisoryDebtAccount[];
  /** IBANs that are not in cache and need to be fetched from API */
  missingIbans: string[];
}

/**
 * Returns cached positions and the list of IBANs not yet in cache.
 *
 * Used by the advisory positions endpoint to merge cached data with a request for
 * only missing IBANs (or all when payload is undefined). Pure function: no
 * refetch/forced behaviour; the API layer handles forceRefetch.
 *
 * @example Const ibans = selectClientProductsAccountsByInstitutionId(state,
 * institution_id); const { cached, missingIbans } = getCachedPositionsDiff(state,
 * 'EUR', clientId, ibans); if (missingIbans.length === 0) return { data: cached };
 * const response = await fetchPositions(missingIbans); return { data:
 * cached.concat(response.data) };
 *
 * @param state - Redux state (RootAdvisoryRepositoryState)
 * @param currency - Account currency
 * @param clientId - Client ID
 * @param ibans - IBANs to resolve (e.g. from
 *   selectClientProductsAccountsByInstitutionId)
 * @returns `{ cached, missingIbans }` – cached positions for IBANs in cache, IBANs
 *   to request
 */
export function getCachedPositionsDiff(
  state: RootAdvisoryRepositoryState,
  currency: AccountCurrency,
  clientId: number,
  ibans: string[],
): CachedPositionsDiff {
  if (!ibans.length) {
    return { cached: [], missingIbans: [] };
  }

  const cached = selectAdvisoryPositionsByIbans(state, currency, clientId, ibans);

  const cachedIbans = new Set(cached.map(item => item.iban).filter(Boolean));
  const missingIbans = ibans.filter(iban => !cachedIbans.has(iban));

  return {
    cached,
    missingIbans,
  };
}

/**
 * Returns cached historical balances and the list of IBANs not yet in cache for the
 * date range.
 *
 * Used by the advisory historical balances endpoint to merge cached data with a
 * request for only missing IBANs. Pure function; no forced/refetch parameter.
 *
 * @param state - Redux state (RootAdvisoryRepositoryState)
 * @param currency - Account currency
 * @param clientId - Client ID
 * @param ibans - IBANs to resolve (e.g. from
 *   selectClientProductsAccountsByInstitutionId)
 * @param startDate - Start date for the range (ISO 8601)
 * @param endDate - End date for the range (ISO 8601)
 * @returns `{ cached, missingIbans }` – cached balances for IBANs in cache, IBANs
 *   to request
 */
export function getCachedBalancesHistoricalDiff(
  state: RootAdvisoryRepositoryState,
  currency: AccountCurrency,
  clientId: number,
  ibans: string[],
  startDate: string,
  endDate: string,
): CachedBalancesHistoricalDiff {
  if (!ibans.length) {
    return { cached: [], missingIbans: [] };
  }

  const cached = selectAdvisoryBalancesHistoricalByIbans(
    state,
    currency,
    clientId,
    ibans,
    startDate,
    endDate,
  );

  const cachedIbans = new Set(cached.map(item => item.iban).filter(Boolean));
  const missingIbans = ibans.filter(iban => !cachedIbans.has(iban));

  return {
    cached,
    missingIbans,
  };
}

/**
 * Returns cached transactions and the list of IBANs not yet in cache for the start
 * date.
 *
 * Used by the advisory transactions endpoint to merge cached data with a request
 * for only missing IBANs. Pure function; no forced/refetch parameter.
 *
 * @param state - Redux state (RootAdvisoryRepositoryState)
 * @param currency - Account currency
 * @param clientId - Client ID
 * @param ibans - IBANs to resolve (e.g. from
 *   selectClientProductsAccountsByInstitutionId)
 * @param startDate - Start date for transactions (ISO 8601)
 * @returns `{ cached, missingIbans }` – cached transactions for IBANs in cache,
 *   IBANs to request
 */
export function getCachedTransactionsDiff(
  state: RootAdvisoryRepositoryState,
  currency: AccountCurrency,
  clientId: number,
  ibans: string[],
  startDate: string,
): CachedTransactionsDiff {
  if (!ibans.length) {
    return { cached: [], missingIbans: [] };
  }

  const cached = selectAdvisoryTransactionsByIbans(
    state,
    currency,
    clientId,
    ibans,
    startDate,
  );

  const cachedIbans = new Set(cached.map(item => item.iban).filter(Boolean));
  const missingIbans = ibans.filter(iban => !cachedIbans.has(iban));

  return {
    cached,
    missingIbans,
  };
}

/**
 * Returns cached accounts debt and the list of IBANs not yet in cache for the date
 * range.
 *
 * @param state - Redux state (RootAdvisoryRepositoryState)
 * @param currency - Account currency
 * @param clientId - Client ID
 * @param ibans - IBANs to resolve (e.g. from
 *   selectClientProductsAccountsByInstitutionId)
 * @param startDate - Start date for the range (ISO 8601)
 * @param endDate - End date for the range (ISO 8601)
 * @returns `{ cached, missingIbans }` – cached debt accounts for IBANs in cache,
 *   IBANs to request
 */
export function getCachedAccountsDebtDiff(
  state: RootAdvisoryRepositoryState,
  currency: AccountCurrency,
  clientId: number,
  ibans: string[],
  startDate: string,
  endDate: string,
): CachedAccountsDebtDiff {
  if (!ibans.length) {
    return { cached: [], missingIbans: [] };
  }

  const cached = selectAdvisoryAccountsDebtByIbans(
    state,
    currency,
    clientId,
    ibans,
    startDate,
    endDate,
  );

  const cachedIbans = new Set(cached.map(item => item.iban).filter(Boolean));
  const missingIbans = ibans.filter(iban => !cachedIbans.has(iban));

  return {
    cached,
    missingIbans,
  };
}

/**
 * Maps API positions data to advisory repository cache entries.
 *
 * Adds cache key (currency, clientId, iban), currency, client_id, institution_id,
 * iban and data for each position. Use with upsertAdvisoryPositions.
 *
 * @param positionsData - Positions from API (after conversion/enrichment)
 * @param currency - Account currency
 * @param clientId - Client ID
 * @returns AdvisoryPositionsState[] for
 *   advisoryRepositorySlice.actions.upsertAdvisoryPositions
 */
export function transformPositionsForCache(
  positionsData: AccountsPositionsData[],
  currency: AccountCurrency,
  clientId: number,
): AdvisoryPositionsState[] {
  return positionsData.map(position => ({
    id: generateAdvisoryPositionsCacheKey(currency, clientId, position.iban),
    currency,
    client_id: clientId,
    institution_id: position.institution_id,
    iban: position.iban,
    data: position,
  }));
}

/**
 * Maps API historical balances to advisory repository cache entries.
 *
 * Adds cache key (currency, clientId, iban, startDate, endDate), currency,
 * client_id, institution_id, iban, start_date, end_date and data. Use with
 * upsertAdvisoryBalancesHistorical.
 *
 * @param balancesData - Historical balances from API
 * @param currency - Account currency
 * @param clientId - Client ID
 * @param startDate - Range start (ISO 8601)
 * @param endDate - Range end (ISO 8601)
 * @returns AdvisoryBalancesHistoricalState[] for upsertAdvisoryBalancesHistorical
 */
export function transformBalancesHistoricalForCache(
  balancesData: AccountHistoricalBalances[],
  currency: AccountCurrency,
  clientId: number,
  startDate: string,
  endDate: string,
): AdvisoryBalancesHistoricalState[] {
  return balancesData.map(balance => ({
    id: generateAdvisoryBalancesHistoricalCacheKey(
      currency,
      clientId,
      balance.iban,
      startDate,
      endDate,
    ),
    currency,
    client_id: clientId,
    institution_id: balance.institution_id,
    iban: balance.iban,
    start_date: startDate,
    end_date: endDate,
    data: balance,
  }));
}

/**
 * Maps API transactions data to advisory repository cache entries.
 *
 * Adds cache key (currency, clientId, iban, startDate), currency, client_id,
 * institution_id, iban, start_date and data. Use with upsertAdvisoryTransactions.
 *
 * @param transactionsData - Transactions from API (after conversion/enrichment)
 * @param currency - Account currency
 * @param clientId - Client ID
 * @param startDate - Start date for the series (ISO 8601)
 * @returns AdvisoryTransactionsState[] for
 *   advisoryRepositorySlice.actions.upsertAdvisoryTransactions
 */
export function transformTransactionsForCache(
  transactionsData: AccountsTransactionsData[],
  currency: AccountCurrency,
  clientId: number,
  startDate: string,
): AdvisoryTransactionsState[] {
  return transactionsData.map(transaction => ({
    id: generateAdvisoryTransactionsCacheKey(
      currency,
      clientId,
      transaction.iban,
      startDate,
    ),
    currency,
    client_id: clientId,
    institution_id: transaction.institution_id,
    iban: transaction.iban,
    start_date: startDate,
    data: transaction,
  }));
}

/**
 * Maps API accounts debt response to advisory repository cache entries.
 *
 * One state entry per account with IBAN. Top-level `dates` are not stored on the
 * entity.
 */
export function transformAccountsDebtForCache(
  debtResponse: AdvisoryAccountsDebt,
  currency: AccountCurrency,
  clientId: number,
  startDate: string,
  endDate: string,
): AdvisoryAccountsDebtState[] {
  return debtResponse.accounts
    .filter((account): account is AdvisoryDebtAccount & { iban: string } =>
      Boolean(account.iban),
    )
    .map(account => ({
      id: generateAdvisoryAccountsDebtCacheKey(
        currency,
        clientId,
        account.iban,
        startDate,
        endDate,
      ),
      currency,
      client_id: clientId,
      institution_id: account.institution_id,
      iban: account.iban,
      start_date: startDate,
      end_date: endDate,
      data: account,
    }));
}
